import { useQuery } from "@tanstack/react-query";
import { BalanceApi } from "@/openapi/generated";
import type { BalanceScrollQuery } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { useAccessToken } from "@/hooks";

interface UseBalanceSummaryGetProps {
  balanceScrollQuery: BalanceScrollQuery;
  page?: number;
  pageSize?: number;
  lastSelectedId?: number;
  active?: boolean;
  enabled?: boolean;
}

export const useBalanceSummaryGet = ({
  balanceScrollQuery,
  page = 1,
  pageSize = 100,
  lastSelectedId,
  active,
  enabled = true,
}: UseBalanceSummaryGetProps) => {
  const accessToken = useAccessToken();

  const { data, isError, isLoading, isSuccess, isPending, refetch, isFetching } =
    useQuery({
      queryKey: [
        QueryKeys.BalanceSummary,
        balanceScrollQuery,
        page,
        pageSize,
        lastSelectedId,
        active,
      ],
      queryFn: async () => {
        if (!accessToken) {
          throw new Error("Authentication token is required");
        }
        const service = createAuthenticatedApi(BalanceApi, accessToken);
        return service.apiBalanceSummaryPost({
          balanceScrollQuery,
          page,
          pageSize,
          lastSelectedId,
          active,
        });
      },
      enabled: enabled !== false && !!accessToken,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    refetch,
    isFetching,
  };
};
